import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/pro-solid-svg-icons";
import { ScoreUploaderState } from "../../../../state/admin/score-uploader/ScoreUploaderState";
import { ScoreUploaderStep } from "../../../../types/admin/score-uploader/ScoreUploaderStep";
import scoreUploadApi from "../../../../api/dashboard/scoreUploadApi";
import { useAuth } from "react-oidc-context";
import { IDENTIFIER_TYPE_DISPLAY_NAMES } from "../../../../types/admin/score-uploader/IdentifierTypeDisplayNames";
import FormattedDate from "../../../../components/common/FormattedDate";

interface ScoreUploaderConfirmationProps {
  state: ScoreUploaderState;
  onStateChange: (
    state: ScoreUploaderState | Partial<ScoreUploaderState>
  ) => void;
}

const ScoreUploaderConfirmation: React.FC<ScoreUploaderConfirmationProps> = ({
  state,
  onStateChange,
}) => {
  const auth = useAuth();
  const scoreApi = new scoreUploadApi(auth.user?.access_token);

  const handleSubmit = () => {
    if (!state.uploadId) {
      console.error("No upload ID found");
      return;
    }

    onStateChange({
      ...state,
      isLoading: true,
    });

    scoreApi.markUploadReadyForProcessing(
      state.uploadId,
      () => {
        // On success, move to processing step
        onStateChange({
          ...state,
          isLoading: false,
          currentStep: ScoreUploaderStep.Processing,
        });
      },
      (error) => {
        // On error, show error state but stay on current step
        onStateChange({
          ...state,
          isLoading: false,
          error: "Failed to start processing. Please try again.",
        });
        console.error("Error marking upload as ready:", error);
      }
    );
  };

  const handleBack = () => {
    onStateChange({
      ...state,
      currentStep: ScoreUploaderStep.MapColumns,
    });
  };

  const actualRowCount = state.hasHeaderRow
    ? (state.totalRows || 0) - 1
    : state.totalRows || 0;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="bg-blue-50 p-4 rounded-lg flex items-start text-sm">
        <FontAwesomeIcon
          icon={faInfoCircle}
          className="text-blue-500 mt-1 mr-3"
        />
        <div className="space-y-2">
          <p className="text-blue-700 font-medium">
            You're about to upload scores for {actualRowCount} employees
          </p>
          <p className="text-blue-600">
            Please review the details below carefully. Once confirmed, the
            scores will be updated in the system.
          </p>
        </div>
      </div>

      <div className="space-y-6 rounded-lg border border-gray-200 bg-white p-6">
        <div className="flex items-center justify-between">
          <p className="text-xl font-semibold text-gray-900">File Summary</p>
          <span className="px-2.5 py-1 text-sm bg-gray-100 text-gray-600 rounded-full">
            {state.totalRows || 0}{" "}
            {(state.totalRows || 0) === 1 ? "row" : "rows"}
          </span>
        </div>

        <div className="space-y-3">
          <div className="flex items-center py-2 border-b border-gray-100">
            <p className="text-gray-500 w-48">File Name</p>
            <p className="text-gray-900 font-medium">
              {state.uploadedFile?.name}
            </p>
          </div>
          <div className="flex items-center py-2 border-b border-gray-100">
            <p className="text-gray-500 w-48">Contains Header Row</p>
            <p className="text-gray-900 font-medium">
              {state.hasHeaderRow ? "Yes" : "No"}
            </p>
          </div>
          <div className="flex items-center py-2 border-b border-gray-100">
            <p className="text-gray-500 w-48">Score Date</p>
            <p className="text-gray-900 font-medium">
              <FormattedDate
                date={state.dataDate}
                displayMode="DATE-ONLY"
                className="font-medium"
              />
            </p>
          </div>
          <div className="flex items-center py-2 border-b border-gray-100">
            <p className="text-gray-500 w-48">Identifier Type</p>
            <p className="text-gray-900 font-medium">
              Column {state.identifierColumn} (
              {state.identifierType
                ? IDENTIFIER_TYPE_DISPLAY_NAMES[state.identifierType]
                : ""}
              )
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-6 rounded-lg border border-gray-200 bg-white p-6">
        <div className="flex items-center justify-between">
          <p className="text-xl font-semibold text-gray-900">Score Mappings</p>
          <span className="px-2.5 py-1 text-sm bg-gray-100 text-gray-600 rounded-full">
            {state.scoreMappings.length}{" "}
            {state.scoreMappings.length === 1 ? "column" : "columns"}
          </span>
        </div>

        <div className="space-y-3">
          {state.scoreMappings.map((mapping) => {
            const mappingOption = state.availableMappings?.find(
              (option) => option.id.toString() === mapping.dataPointId
            );

            return (
              <div
                key={mapping.id}
                className="flex items-center py-2 border-b border-gray-100"
              >
                <p className="text-gray-500 w-48">Column {mapping.columnId}</p>
                <p className="text-gray-900 font-medium">
                  {mappingOption?.name || "Unknown Score Type"}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex justify-between">
        <button
          onClick={handleBack}
          className="btn-secondary"
          disabled={state.isLoading}
        >
          Previous
        </button>
        <button
          onClick={handleSubmit}
          disabled={state.isLoading}
          className="btn-primary relative"
        >
          {state.isLoading ? (
            <span className="flex items-center">
              <span className="animate-pulse">Processing Upload...</span>
            </span>
          ) : (
            "Confirm and Upload"
          )}
        </button>
      </div>
    </div>
  );
};

export default ScoreUploaderConfirmation;
